import { type MutableRefObject, useCallback, useRef } from 'react';
import { useConst } from '@chakra-ui/react';

import {
    INPUT_BILLING_ANSWER_ID,
    INPUT_DEAL_NAME_TEMPLATE_ID,
    INPUT_DESCRIPTION_ID,
    INPUT_NAME_ID,
    INPUT_SUBCATEGORY_ID,
} from '../../constants';

export type HtmlFormInput<T> = {
    input: MutableRefObject<HTMLInputElement>;
    defaultValue: T;
    onChange: (_: T) => void;
};

export const useHtmlFormInputs = () => {
    // Subcategory
    const inputSubcategory = useRef<HTMLInputElement>(
        document.getElementById(INPUT_SUBCATEGORY_ID) as HTMLInputElement,
    );
    const defaultValueSubcategory = useConst(
        Boolean(inputSubcategory.current?.value) &&
            typeof inputSubcategory.current?.value === 'string' &&
            !isNaN(parseInt(inputSubcategory.current?.value, 10))
            ? parseInt(inputSubcategory.current?.value, 10)
            : null,
    );
    const onChangeSubcategory = useCallback(
        (value: number | null) => {
            if (inputSubcategory.current === null) return;
            inputSubcategory.current.value = String(value ?? '');
        },
        [inputSubcategory],
    );

    // Billing answer
    const inputBillingAnswer = useRef<HTMLInputElement>(
        document.getElementById(INPUT_BILLING_ANSWER_ID) as HTMLInputElement,
    );
    const defaultValueBillingAnswer = useConst<string | null>(
        () => inputBillingAnswer.current?.value ?? null,
    );
    const onChangeBillingAnswer = useCallback(
        (value: string | null) => {
            if (inputBillingAnswer.current === null) return;
            inputBillingAnswer.current.value = String(value ?? '');
        },
        [inputBillingAnswer],
    );

    // Name
    const inputName = useRef<HTMLInputElement>(
        document.getElementById(INPUT_NAME_ID) as HTMLInputElement,
    );
    const defaultValueName = useConst(() => inputName.current?.value ?? '');
    const onChangeName = useCallback(
        (value: string) => {
            if (inputName.current === null) return;
            inputName.current.value = value;
        },
        [inputName],
    );

    // Deal name
    const inputDealNameTemplate = useRef<HTMLInputElement>(
        document.getElementById(INPUT_DEAL_NAME_TEMPLATE_ID) as HTMLInputElement,
    );
    const defaultValueDealNameTemplate = useConst(
        Boolean(inputDealNameTemplate.current?.value) &&
            typeof inputDealNameTemplate.current?.value === 'string' &&
            !isNaN(parseInt(inputDealNameTemplate.current?.value, 10))
            ? parseInt(inputDealNameTemplate.current?.value, 10)
            : null,
    );
    const onChangeDealNameTemplate = useCallback(
        (value: number | null) => {
            if (inputDealNameTemplate.current === null) return;
            inputDealNameTemplate.current.value = String(value ?? '');
        },
        [inputDealNameTemplate],
    );

    // Description
    const inputDescription = useRef<HTMLInputElement>(
        document.getElementById(INPUT_DESCRIPTION_ID) as HTMLInputElement,
    );
    const defaultValueDescription = useConst(() => inputDescription.current?.value ?? '');
    const onChangeDescription = useCallback(
        (value: string) => {
            if (inputDescription.current === null) return;
            inputDescription.current.value = value;
        },
        [inputDescription],
    );

    return {
        subcategory: {
            input: inputSubcategory,
            defaultValue: defaultValueSubcategory,
            onChange: onChangeSubcategory,
        },

        billing_answer: {
            input: inputBillingAnswer,
            defaultValue: defaultValueBillingAnswer,
            onChange: onChangeBillingAnswer,
        },

        name: {
            input: inputName,
            defaultValue: defaultValueName,
            onChange: onChangeName,
        },

        deal_name_template: {
            input: inputDealNameTemplate,
            defaultValue: defaultValueDealNameTemplate,
            onChange: onChangeDealNameTemplate,
        },

        description: {
            input: inputDescription,
            defaultValue: defaultValueDescription,
            onChange: onChangeDescription,
        },
    };
};
